import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  InputLabel,
  MenuItem,
  OutlinedInput,
  Radio,
  RadioGroup,
  Select,
  SelectChangeEvent,
  TextField,
  Typography,
  Divider,
  Paper,
  Chip,
  Tooltip,
} from '@mui/material';
import { 
  Send as SendIcon, 
  InfoOutlined as InfoIcon,

} from '@mui/icons-material';
import { ClientGroup } from '../../../types/client';
import { getExpertBulkNotificationPreview } from '../../../services/notification.service';
import { ModalIndices } from '../../../common/i-doc/i-doc.registry.ts';

export type NotificationLevel = 'info' | 'warning' | 'danger';

export interface BulkNotificationData {
  message: string;
  level: NotificationLevel;
  targetType: 'all' | 'groups';
  groupId: number | null;
}



interface BulkNotificationModalProps {
  open: boolean;
  onClose: () => void;
  onSend: (data: BulkNotificationData) => Promise<void>;
  groups: ClientGroup[];
}

export const BulkNotificationModal: React.FC<BulkNotificationModalProps> = ({
  open,
  onClose,
  onSend,
  groups,
}) => {
  const [message, setMessage] = useState('');
  const [level, setLevel] = useState<NotificationLevel>('info');
  const [targetType, setTargetType] = useState<'all' | 'groups'>('all');
  const [selectedGroupId, setSelectedGroupId] = useState<number | ''>('');
  const [sending, setSending] = useState(false);
  
  const [recipients, setRecipients] = useState<{ id: number; name: string }[]>([]);
  const [loadingRecipients, setLoadingRecipients] = useState(false);

  useEffect(() => {
    if (open) {
      fetchRecipients();
    }
  }, [targetType, selectedGroupId, open]);

  const fetchRecipients = async () => {
    if (targetType === 'groups' && !selectedGroupId) {
      setRecipients([]);
      return;
    }
    
    setLoadingRecipients(true);
    try {
      const list = await getExpertBulkNotificationPreview({
        targetType,
        groupId: targetType === 'groups' ? (selectedGroupId as number) : undefined,
      });
      setRecipients(list);
    } catch (error) {
      console.error('Failed to fetch recipients preview:', error);
    } finally {
      setLoadingRecipients(false);
    }
  };

  const handleSend = async () => {
    if (!message.trim() || recipients.length === 0) return;
    if (targetType === 'groups' && !selectedGroupId) return;

    setSending(true);
    try {
      await onSend({
        message: message.trim(),
        level,
        targetType,
        groupId: targetType === 'groups' ? (selectedGroupId as number) : null,
      });
      handleClose();
    } catch (error) {
      console.error(error);
    } finally {
      setSending(false);
    }
  };

  const handleClose = () => {
    setMessage('');
    setLevel('info');
    setTargetType('all');
    setSelectedGroupId('');
    setRecipients([]);
    onClose();
  };

  return (
    <Dialog 
      open={open} 
      onClose={handleClose} 
      fullWidth 
      maxWidth="md"
      data-i-doc={ModalIndices.BULK_NOTIFICATION_CREATE}
    >
      <DialogTitle sx={{ pb: 1 }}>Створити масове сповіщення</DialogTitle>
      <Divider />
      <DialogContent sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, gap: 4, pt: 3 }}>
        
        {/* Form Section */}
        <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 3 }}>
          <Box>
            <Typography variant="subtitle2" color="text.secondary" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              1. Кому надіслати?
              {loadingRecipients && <CircularProgress size={14} />}
            </Typography>
            <RadioGroup
              row
              value={targetType}
              onChange={(e) => setTargetType(e.target.value as 'all' | 'groups')}
            >
              <FormControlLabel value="all" control={<Radio />} label="Всім клієнтам" />
              <FormControlLabel value="groups" control={<Radio />} label="Вибраній групі" />
            </RadioGroup>
          </Box>

          {targetType === 'groups' && (
            <FormControl fullWidth size="small">
              <InputLabel>Виберіть групу</InputLabel>
              <Select
                value={selectedGroupId}
                onChange={(e) => setSelectedGroupId(e.target.value as number)}
                input={<OutlinedInput label="Виберіть групу" />}
              >
                {groups.map((group) => (
                  <MenuItem key={group.id} value={group.id}>
                    {group.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          )}

          {/* Recipients Preview */}
          <Box sx={{ mt: -1 }}>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
              Буде надіслано ({recipients.length}):
            </Typography>
            <Box sx={{ 
              display: 'flex', 
              flexWrap: 'wrap', 
              gap: 0.5, 
              maxHeight: 100, 
              overflowY: 'auto',
              p: 1,
              bgcolor: 'action.hover',
              borderRadius: 1,
              border: '1px dashed',
              borderColor: 'divider'
            }}>
              {recipients.length > 0 ? (
                recipients.map(r => (
                  <Chip key={r.id} label={r.name} size="small" variant="outlined" sx={{ bgcolor: 'background.paper' }} />
                ))
              ) : (
                <Typography variant="caption" color="error">
                  {loadingRecipients ? 'Завантаження...' : 'Немає активних отримувачів у вибраній категорії'}
                </Typography>
              )}
            </Box>
          </Box>

          <Box>
            <Typography variant="subtitle2" color="text.secondary" gutterBottom>
              2. Налаштування повідомлення
            </Typography>
            <FormControl fullWidth size="small" sx={{ mb: 2 }}>
              <InputLabel>Тип сповіщення</InputLabel>
              <Select
                value={level}
                label="Тип сповіщення"
                onChange={(e: SelectChangeEvent) => setLevel(e.target.value as NotificationLevel)}
              >
                <MenuItem value="info">Інформаційне (синій)</MenuItem>
                <MenuItem value="warning">Попередження (жовтий)</MenuItem>
                <MenuItem value="danger">Важливо (червоний)</MenuItem>
              </Select>
            </FormControl>

            <TextField
              label="Текст повідомлення"
              multiline
              rows={5}
              fullWidth
              placeholder="Клієнт отримає це сповіщення у мобільному застосунку..."
              value={message}
              onChange={e => setMessage(e.target.value)}
              variant="outlined"
            />
          </Box>
        </Box>

        {/* Preview Section */}
        <Box sx={{ width: { md: 300 }, display: 'flex', flexDirection: 'column', gap: 2 }}>
          <Typography variant="subtitle2" color="text.secondary">
            Попередній перегляд (як побачить клієнт)
          </Typography>
          <Paper 
            variant="outlined" 
            sx={{ 
              p: 2, 
              borderRadius: 2, 
              bgcolor: 'background.default',
              borderLeft: 6,
              borderColor: level === 'info' ? 'info.main' : level === 'warning' ? 'warning.main' : 'error.main'
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
              <InfoIcon fontSize="small" color={level as any} />
              <Typography variant="caption" fontWeight="bold" sx={{ textTransform: 'uppercase' }}>
                {level === 'info' ? 'Інфо' : level === 'warning' ? 'Увага' : 'Важливо'}
              </Typography>
            </Box>
            <Typography variant="body2" sx={{ wordBreak: 'break-word', whiteSpace: 'pre-wrap' }}>
              {message || 'Тут буде текст вашого сповіщення...'}
            </Typography>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1, textAlign: 'right' }}>
              Тільки що
            </Typography>
          </Paper>
          
          <Box sx={{ mt: 'auto', p: 2, bgcolor: 'action.hover', borderRadius: 2 }}>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'flex', gap: 1 }}>
              <InfoIcon sx={{ fontSize: 16 }} />
              Після надсилання сповіщення неможливо відредагувати або видалити.
            </Typography>
          </Box>
        </Box>
      </DialogContent>
      <Divider />
      <DialogActions sx={{ px: 3, py: 2 }}>
        <Button onClick={handleClose} color="inherit">Скасувати</Button>
        <Tooltip title={recipients.length === 0 ? "Немає отримувачів" : ""}>
          <span>
            <Button
              variant="contained"
              startIcon={sending ? <CircularProgress size={20} /> : <SendIcon />}
              onClick={handleSend}
              disabled={sending || !message.trim() || (targetType === 'groups' && !selectedGroupId) || recipients.length === 0}
              sx={{ px: 4 }}
            >
              {sending ? 'Надсилається...' : `Надіслати (${recipients.length})`}
            </Button>
          </span>
        </Tooltip>
      </DialogActions>
    </Dialog>
  );
};
